import { useState } from 'react'
import { useRequestTranslationBatch } from '@/hooks/useMessages'
import type { MatrixRow } from './matrix-model'
import {
	countOverwrites,
	liveSelection,
	selectableKeysInColumn,
	selectableKeysInRow,
	selectionToBatchItems,
	tickableRows,
	validateBatchLimits,
} from './matrix-model'
import { buildRequestKey } from './request-key'

/**
 * Immutable toggle on the ticked-cell set. A group of keys moves as one: it is
 * cleared only when every key in it is already ticked, so a half-ticked row or
 * column fills in rather than emptying.
 */
function toggleKeys(selection: ReadonlySet<string>, keys: readonly string[]): Set<string> {
	const next = new Set(selection)
	if (keys.every((key) => next.has(key))) for (const key of keys) next.delete(key)
	else for (const key of keys) next.add(key)
	return next
}

/**
 * The matrix's tick-then-translate action: which cells are selected, and the one
 * batch call that queues them all. Cells tick one at a time, a whole row at once
 * (everything one message can be translated into) or a whole column at once
 * (every listed message for one locale).
 *
 * The selection is keyed by `(sourceMessageId, locale)` rather than by a cell
 * index, so it survives a column being added or the rows being refetched in a
 * different order. Every ticked cell for one message collapses into a single
 * batch item, which is the shape `POST /messages/translations/batch` takes.
 *
 * Ticks are held as raw keys but read through `liveSelection`, so a row that
 * leaves the grid takes its ticks out of the count and out of the batch without
 * losing them if it comes back.
 */
export function useMatrixBatchTranslate(rows: readonly MatrixRow[], editingRowId: string | null = null) {
	const requestBatch = useRequestTranslationBatch()
	const [selection, setSelection] = useState<ReadonlySet<string>>(new Set())
	const [batchError, setBatchError] = useState<string | null>(null)
	// Overwriting cells that already hold text has no undo — name the count and
	// let the user back out through the matrix's ConfirmDialog before queueing.
	const [pendingOverwrites, setPendingOverwrites] = useState<number | null>(null)
	const live = liveSelection(rows, selection)

	const toggle = (keys: readonly string[]) => {
		setBatchError(null)
		setSelection((prev) => toggleKeys(prev, keys))
	}

	const toggleCell = (sourceMessageId: string, locale: string) => toggle([buildRequestKey(sourceMessageId, locale)])
	/** Everything this piece of copy can be translated into, in one tick. */
	const toggleRow = (row: MatrixRow) => toggle(selectableKeysInRow(row))
	/** Every listed piece of copy that can be translated into this locale, in one tick. */
	const toggleLocale = (locale: string) => toggle(selectableKeysInColumn(tickableRows(rows, editingRowId), locale))

	const submitBatch = () => {
		const items = selectionToBatchItems(live)
		// The confirm dialog blocks edits, but a background refetch can prune the
		// selection between open and confirm — an emptied batch sends nothing.
		if (items.length === 0) return
		setBatchError(null)
		// Clear only on success: a failed submit leaves the ticks in place so the
		// user can retry without re-picking every cell.
		requestBatch.mutate(items, { onSuccess: () => setSelection(new Set()) })
	}

	const applyTranslation = () => {
		const items = selectionToBatchItems(live)
		if (items.length === 0) return
		const limitError = validateBatchLimits(items)
		if (limitError) return setBatchError(limitError)
		const overwrites = countOverwrites(rows, live)
		if (overwrites > 0) return setPendingOverwrites(overwrites)
		submitBatch()
	}

	return {
		selection: live,
		selectedCount: live.size,
		toggleCell,
		toggleRow,
		toggleLocale,
		clearSelection: () => setSelection(new Set()),
		applyTranslation,
		pendingOverwrites,
		confirmApplyOverwrites: () => {
			setPendingOverwrites(null)
			submitBatch()
		},
		cancelApplyOverwrites: () => setPendingOverwrites(null),
		isApplying: requestBatch.isPending,
		// The batch endpoint reports per-item outcomes, so a resolved call can still
		// hold failures; the cells themselves carry the authoritative state.
		batchError:
			batchError ?? (requestBatch.isError ? 'Some translations failed to queue. Check each cell below.' : null),
	}
}
